// frontend/src/components/RelanceClientButton.js
//
// Chase an unpaid facture by SMS.
//
// The dashboard has had a "Relancer les clients" tile since it was built,
// linking to the invoice list with nothing behind it. This is the action it
// was pointing at.
//
// It shows when the client was last chased, because that is the counter's real
// question — "have we already called this one?" — and because a button that
// looks the same before and after sending invites a second click that costs
// another SMS.

import React, { useState } from 'react';
import axios from 'axios';
import API_URL from '../api/baseURL';

/** "il y a 3 h" — staleness matters here, the exact minute does not. */
function since(date) {
  if (!date) return null;
  const mins = Math.floor((Date.now() - new Date(date).getTime()) / 60000);
  if (Number.isNaN(mins)) return null;
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  return `il y a ${d} j`;
}

export default function RelanceClientButton({ invoiceId, token, lastReminderAt, reminderCount, onSent }) {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [sentAt, setSentAt] = useState(lastReminderAt || null);
  const [count, setCount] = useState(reminderCount || 0);

  const relancer = async () => {
    setLoading(true);
    setErr('');
    setMsg('');
    try {
      const res = await axios.patch(
        `${API_URL}/workshop-invoices/invoice/${invoiceId}/remind`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSentAt(res.data?.lastReminderAt || new Date().toISOString());
      setCount(res.data?.reminderCount || count + 1);
      // The reply distinguishes "texted" from "recorded but no phone" — saying
      // "Relancé" for a client with no number on file would be a lie.
      setMsg(res.data?.texted ? 'Relancé' : (res.data?.hasPhone ? 'SMS échoué' : 'Sans téléphone'));
      if (onSent) onSent(res.data);
    } catch (e) {
      // 429 is the cooldown, and its message already says when to retry.
      setErr(e.response?.data?.error || 'Échec de la relance');
    } finally {
      setLoading(false);
    }
  };

  const ago = since(sentAt);

  return (
    <span className="inline-flex flex-col items-end align-middle">
      <button
        onClick={relancer}
        disabled={loading}
        title={ago ? `Dernière relance ${ago}` : 'Envoyer un rappel de paiement par SMS'}
        className="h-[26px] px-2.5 rounded-[7px] text-[11px] font-medium whitespace-nowrap transition-colors
                   border border-[#3A3327] bg-[#181510] text-[#DFA23C] hover:border-[#DFA23C]/50
                   disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Envoi…' : msg || 'Relancer'}
      </button>
      {err ? (
        <span className="text-[10px] text-[#E2574B] mt-0.5 max-w-[190px] text-right leading-tight">{err}</span>
      ) : ago ? (
        <span className="text-[10px] text-[#6E665C] mt-0.5">
          {count > 1 ? `${count} relances · ` : ''}{ago}
        </span>
      ) : null}
    </span>
  );
}
